import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { Observable, Subject, takeUntil } from 'rxjs';

import { ActivitiesService } from 'app/modules/admin/activities/activities.service';
import { Activity } from 'app/modules/admin/activities/activities.types';
import { BreadcrumbComponent } from 'app/components/breadcrumb/breadcrumb.component';
import { FooterComponent } from 'app/components/footer/footer.component';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { Location } from '@angular/common';
import { LogoComponent } from 'app/components/logo/logo.component';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { UserComponent } from 'app/layout/common/user/user.component';
import { clubConfig } from 'config/club.config';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    styleUrls: ['./dense.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        FooterComponent,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        LogoComponent,
        MatBadgeModule,
        MatButtonModule,
        MatIconModule,
        RouterOutlet,
        UserComponent,
        NotificationsComponent,
        BreadcrumbComponent
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
    [x: string]: any;
    isScreenSmall: boolean;
    hidden: boolean = false;
    navigation: Navigation;
    navigationAppearance: 'default' | 'dense' = 'dense';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    menuHidden: boolean = false;
    clubName!: any;
    isScrolled = false;
    // isCardScrolled = false;
    private ticking = false;
    logoVisible = false;


    // Pull to refresh properties
    pullProgress = 0;
    pullThreshold = 80; // Seuil pour déclencher le refresh
    isRefreshing = false;
    private startY = 0;
    private currentY = 0;
    private isPulling = false;
    private maxPullDistance = 150;
    headerHeight = 96;


    club = clubConfig[0];
    @ViewChild('headerElement') headerElement: ElementRef;
    @ViewChild('menuBurger') menuBurger: ElementRef;
    @ViewChild('logoContainer') logoContainer: ElementRef;
    @ViewChild('fuseVerticalNav') fuseVerticalNav: FuseVerticalNavigationComponent;


    scrollThreshold = 100; // Le seuil à partir duquel on considère le défilement
    forceScrollCheck = false; // Pour forcer une vérification

    activities$: Observable<Activity[]>;
    activities!: any;
    locale: string = 'fr';
    isAndroidApp = false;
    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _location: Location,
    ) {
        // console.log('location', this._location.path())
        if (
            /^(http)[:\/\-.1-9a-zA-z]+(\/product\/(\d)+)/.test(
                this._location.path()
            ) ||
            /\/product\/(\d+)$/.test(this._location.path()) ||
            /(\/flash-memory)$/.test(this._location.path())
        ) {
            this.menuHidden = true;
        } else {
            this.menuHidden = false;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.clubName = clubConfig[0];
        // Détection Android
        const userAgent = navigator.userAgent.toLowerCase();
        this.isAndroidApp =
            /android/.test(userAgent) &&
            (userAgent.includes('wv') || userAgent.includes('version/'));

        if (this.isAndroidApp) {
            document.body.classList.add('android-app');
        }
        // console.log(this.clubName.name);
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Utiliser une requête média personnalisée pour définir 1280px comme seuil
        this._fuseMediaWatcherService
            .onMediaQueryChange$('(min-width: 1280px)')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((state) => {
                // Inverser la logique car onMediaQueryChange$ renvoie true quand la requête correspond
                this.isScreenSmall = !state.matches;

                // Déterminer l'apparence de la navigation
                this.navigationAppearance = this.isScreenSmall
                    ? 'default'
                    : 'dense';
            });

        this.adjustScrollThreshold();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    ngAfterViewInit() {
        // Vérifier si le contenu est suffisant pour permettre le défilement
        setTimeout(() => {
            this.ensureScrollableContent();
            this.fuseVerticalNav.refresh();
            this.calculateHeaderHeight();

        }, 500); // Délai pour s'assurer que le DOM est complètement chargé
    }

    getIndicatorTransform(): string {
        const translateY = Math.min(this.pullProgress - 64, 0);
        return `translateY(${translateY}px)`;
    }


    getContentTransform(): string {
        return this.pullProgress > 0 ? `${this.pullProgress}px` : '0px';
    }

    private calculateHeaderHeight(): void {
        if (this.headerElement) {
            this.headerHeight = this.headerElement.nativeElement.offsetHeight;
        }
    }

    // Pull to Refresh Methods
    onContentTouchStart(event: TouchEvent): void {
        // Vérifier si on est en haut du contenu principal
        if (this.getContentScrollTop() > 0 || this.isRefreshing) return;

        this.startY = event.touches[0].clientY;
        this.isPulling = true;
    }

    onContentTouchMove(event: TouchEvent): void {
        if (!this.isPulling || this.isRefreshing) return;

        this.currentY = event.touches[0].clientY;
        const deltaY = this.currentY - this.startY;

        // Seulement si on tire vers le bas ET qu'on est en haut du contenu
        if (deltaY > 0 && this.getContentScrollTop() === 0) {
            event.preventDefault();
            this.pullProgress = Math.min(deltaY * 0.5, this.maxPullDistance);
        }
    }

    onContentTouchEnd(event: TouchEvent): void {
        if (!this.isPulling || this.isRefreshing) return;

        this.isPulling = false;

        if (this.pullProgress >= this.pullThreshold) {
            this.triggerRefresh();
        } else {
            this.resetPull();
        }
    }

    private getContentScrollTop(): number {
        // Vérifier le scroll de la fenêtre principale
        return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    }

    private triggerRefresh(): void {
        this.isRefreshing = true;
        this.pullProgress = this.pullThreshold;

        // Simuler un appel API ou déclencher votre logique de refresh
        this.performRefresh().then(() => {
            this.resetPull();
        });
    }

    private async performRefresh(): Promise<void> {
        if (this.routerOutlet?.component && 'refresh' in this.routerOutlet.component) {
            await (this.routerOutlet.component as any).refresh();
            console.log('Products refreshed successfully');
        } else {
            // Remplacez cette logique par votre propre méthode de refresh
            // Exemple : recharger les données, actualiser le cache, etc.
            const currentRoute = this._router.url;
            await this._router.navigateByUrl('/', { skipLocationChange: true });
            await this._router.navigateByUrl(currentRoute);
        }

    }

    private resetPull(): void {
        this.isRefreshing = false;
        this.pullProgress = 0;
        this.startY = 0;
        this.currentY = 0;
    }

    // Méthode publique pour déclencher un refresh programmatiquement
    public refresh(): void {
        if (!this.isRefreshing) {
            this.triggerRefresh();
        }
    }



    @HostListener('window:scroll', ['$event'])
    onWindowScroll() {
        // Utilisation de requestAnimationFrame pour optimiser les performances
        if (!this.ticking) {
            window.requestAnimationFrame(() => {
                this.handleScroll();
                this.ticking = false;
            });
            this.ticking = true;
        }
    }

    private handleScroll() {
        const scrollPosition =
            window.scrollY ||
            document.documentElement.scrollTop ||
            document.body.scrollTop ||
            0;

        // Mettre à jour l'état du défilement
        const wasScrolled = this.isScrolled;
        this.isScrolled = scrollPosition > this.scrollThreshold;

        // Si l'état a changé, déclencher des mises à jour supplémentaires si nécessaire
        if (wasScrolled !== this.isScrolled) {
            this.updateUIForScrollState();
        }
    }

    // Nouvelle méthode pour mettre à jour l'interface utilisateur
    private updateUIForScrollState() {
        // Mettre à jour d'autres éléments d'interface en fonction de l'état de défilement
        if (this.isScrolled) {
            // Actions lors du défilement
            this.logoVisible = true;
        } else {
            // Actions lorsqu'on revient au début
            this.logoVisible = false;
        }
    }

    // Nouvelle méthode pour s'assurer qu'il y a suffisamment de contenu pour défiler
    private ensureScrollableContent() {
        // Obtenir la hauteur de la fenêtre et du document
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // Si le document n'est pas plus grand que la fenêtre, ajouter un espace
        if (documentHeight <= windowHeight + 50) {
            // 50px de marge
            this.forceScrollCheck = true;

            // Ajouter un div temporaire pour augmenter la hauteur
            const spacer = document.createElement('div');
            spacer.style.height = '100vh';
            spacer.style.opacity = '0';
            spacer.className = 'temp-spacer';
            document.body.appendChild(spacer);

            // Nettoyer après un délai
            setTimeout(() => {
                const tempSpacers = document.querySelectorAll('.temp-spacer');
                tempSpacers.forEach((spacer) => spacer.remove());
                this.forceScrollCheck = false;
            }, 2000);
        }
    }

    // Méthode pour forcer le défilement à une position spécifique
    scrollToPosition(
        position: number,
        behavior: ScrollBehavior = 'smooth'
    ): void {
        window.scrollTo({
            top: position,
            behavior: behavior,
        });
    }

    // Méthode pour forcer l'affichage des éléments même sans défilement suffisant
    forceScrolledState(): void {
        if (!this.isScrolled) {
            this.isScrolled = true;
            this.updateUIForScrollState();
        }
    }

    // Nouvelle méthode pour ajuster le seuil de défilement
    private adjustScrollThreshold(): void {
        // Réduire le seuil pour les petits écrans
        if (window.innerHeight < 700) {
            this.scrollThreshold = 30;
        } else if (window.innerHeight > 1000) {
            this.scrollThreshold = 80;
        }
    }

    // Méthode publique pour forcer l'affichage
    forceDisplay(): void {
        if (window.scrollY < this.scrollThreshold) {
            // Si on n'a pas assez défilé, on peut soit:
            // 1. Forcer le défilement
            this.scrollToPosition(this.scrollThreshold + 10);
            // ou 2. Forcer l'état même sans défilement
            // this.forceScrolledState();
        }
    }



    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
    onToggle() {
        this.hidden = !this.hidden;
    }
    scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
    }

    onGoTo($event) {
        console.log('even', $event);
        // window.open('https://cab1921.com', '_blank');
        window.location.href = 'capacitor://localhost?parametre=valeur';
    }
}
